import { Bet } from "interfaces/bet";
import { Entry } from "interfaces/entry";
import { Pool } from "interfaces/pool";
import { useEffect, useRef, useState } from "react";
import cardFlip from "./card-flip.module.scss";
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";
import BlockyBoldText from "components/custom-texts/blocky-bold-text";
import { secondaryColor } from "utils/constants";
import BlockyMediumText from "components/custom-texts/blocky-medium-text";
import NumberFormat from "react-number-format";
import { useAppSelector } from "state/hooks";
import {
  getCardColor,
  getGroupCurrencySymbol,
  getGroupState,
  getTextColor,
} from "state/slices/group-slice";
import {
  formatAsCurrency,
  formattedCurrencyToNumber,
} from "utils/formatter-utils/currency-formatter";
import ChangeHighlight from "react-change-highlight";
import { calculateOdds } from "utils/oddsCalculator";
import { Button, Spinner } from "react-bootstrap";
import confirmBet from "components/pool-details/confirm-bet";
import { getIntegratedAppState } from "state/slices/integrated-app-slice";
import { X } from "react-bootstrap-icons";
import {
  resolveMinBetAmountContestEntry,
  resolveMinBetAmountForContestEntry,
} from "components/pool-details/entries/bet-form-helpers";
import styles from "assets/shared-stylesheets/sparket-list-item.module.css";
import { getBetsState } from "state/slices/bets-slice";
import useWallet from "hooks/useWallet";
import listItemStyles from "assets/shared-stylesheets/sparket-list-item.module.css";

interface Props {
  entry?: Entry | null;
  pool: Pool;
  onSuccessfulBetPlaced: (bet: Bet) => void;
  contestEntryId?: string;
  cancel: () => void;
}

const HomepageBetForm = ({
  entry,
  pool,
  onSuccessfulBetPlaced,
  contestEntryId,
  cancel,
}: Props) => {
  if (!entry) {
    entry = {} as Entry;
  }
  const { formattedBalance, balance } = useWallet({
    contestEntryId,
  });
  const [betAmount, setBetAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const group = useAppSelector(getGroupState);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const inputRef = useRef(null);

  const handleSubmit = () => {
    confirmBet({
      isIntegratedApp,
      betAmount,
      poolEntryId: entry.id || entry.pool_entry_id,
      pool_id: pool.id,
      setLoading,
      groupId: group.id,
      setErrorMessage,
      onSuccessfulBetPlaced: (bet: Bet) => {
        onSuccessfulBetPlaced(bet);
        cancel();
      },
      currency: group.currency_code,
      contest_entry_id: contestEntryId,
    });
  };

  const odds = calculateOdds({
    poolTotal: pool.total_win,
    entryTotal: entry.bet_total || entry.bets_sum || 0,
    betAmount: formattedCurrencyToNumber(betAmount) || 0,
    fee: pool.fee,
  });

  const projectedPayout =
    formattedCurrencyToNumber(betAmount || "0") * (odds || 0);

  const formattedProjectedPayout = formatAsCurrency(projectedPayout);
  const isIntegratedApp = useAppSelector(getIntegratedAppState).isIntegratedApp;
  const currencySymbol = useAppSelector(getGroupCurrencySymbol);
  const { bets } = useAppSelector(getBetsState);
  const contestantIds = (entry?.contestant_id || "")
    .split(";")
    .filter((id) => id.length > 0);
  const selectedContestants = contestantIds.map((id) => {
    const contestant = pool.contestants.find((c) => c.id === id);
    return contestant;
  });

  const numberOfSelections = selectedContestants.length;

  const contestMinAmount = resolveMinBetAmountContestEntry(
    bets || [],
    contestEntryId || "",
    balance || 0,
    numberOfSelections
  );

  const contestMinBetAmount = resolveMinBetAmountForContestEntry(
    bets || [],
    contestEntryId || "",
    numberOfSelections
  );

  useEffect(() => {
    if (inputRef && inputRef.current) {
      (inputRef.current as any).focus({ preventScroll: true });
    }

    return () => {
      if (inputRef) {
        inputRef.current = null;
      }
    };
  }, [inputRef, entry]);

  useEffect(() => {
    const hasBalance = balance !== undefined;
    const hasBets = bets !== undefined;
    const hasContestEntryId = contestEntryId !== undefined;
    const remainingBalance =
      (balance || 0) - formattedCurrencyToNumber(betAmount);

    const showContestMinAmountText =
      hasBalance &&
      hasBets &&
      hasContestEntryId &&
      remainingBalance < contestMinAmount;

    const showMinBetAmountText =
      hasBets &&
      hasContestEntryId &&
      formattedCurrencyToNumber(betAmount) < contestMinBetAmount &&
      contestMinBetAmount > 0;

    if (showContestMinAmountText) {
      setErrorMessage(
        `You must keep at least ${formatAsCurrency(contestMinAmount)} to
       complete your entry with other picks.`
      );
    } else if (showMinBetAmountText) {
      setErrorMessage(
        `Your first two picks must be at least ${formatAsCurrency(
          contestMinBetAmount
        )}`
      );
    } else {
      setErrorMessage(undefined);
    }
  }, [
    betAmount,
    balance,
    bets,
    contestEntryId,
    contestMinAmount,
    contestMinBetAmount,
  ]);

  const textColor = useAppSelector(getTextColor);
  const cardColor = useAppSelector(getCardColor);

  return (
    <div
      className={styles.itemContainer + " " + cardFlip.cardFlipBack}
      style={{
        minHeight: 90,
        marginLeft: 0,
        marginRight: 0,
        height: 250,
        fontSize: 12,
        backgroundColor: cardColor,
        color: textColor,
      }}
    >
      <div
        style={{
          width: "100%",
          padding: 5,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <BlockyHeavyText style={{ fontSize: 10 }}>
              {pool.event_name}
            </BlockyHeavyText>
            <BlockyHeavyText className={listItemStyles.poolNameText}>
              {pool.name}
            </BlockyHeavyText>
          </div>
          <div style={{ marginTop: -5 }} onClick={() => cancel()}>
            <X size={30} />
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {/* Win Condition */}
          <div>
            <BlockyHeavyText className={styles.poolCloseDatePrefix}>
              Win Condition:&nbsp;
            </BlockyHeavyText>
            <BlockyMediumText
              style={{ fontSize: 12, display: "inline", lineHeight: 0.5 }}
            >
              {pool.win_condition}
            </BlockyMediumText>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          {/* Contestant */}
          <div style={{ overflow: "hidden" }}>
            <BlockyHeavyText
              style={{
                fontSize: 16,
                color: secondaryColor,
              }}
            >
              {entry.contestant_name}
            </BlockyHeavyText>
          </div>

          {/* Balance */}
          <div>
            <BlockyHeavyText className={styles.poolCloseDatePrefix}>
              Balance:&nbsp;
            </BlockyHeavyText>
            <BlockyBoldText style={{ fontSize: 12, color: secondaryColor }}>
              {formattedBalance}
            </BlockyBoldText>
          </div>
        </div>

        <div>
          {/* form */}
          {errorMessage && (
            <div style={{ marginTop: 5, color: "red", fontSize: 12 }}>
              {errorMessage}
            </div>
          )}
          <div style={{ display: "flex", padding: 3 }}>
            {/* left */}
            <div style={{ flex: 0.5 }}>
              <NumberFormat
                id="input-BetModalContent-betAmountInput"
                getInputRef={inputRef}
                onValueChange={(value) => {
                  if (!value.floatValue) {
                    setBetAmount("$0.00");
                  }

                  if (
                    balance !== undefined &&
                    formattedCurrencyToNumber(value.formattedValue) <= balance!
                  ) {
                    setBetAmount(value.formattedValue);
                  }
                }}
                prefix={currencySymbol}
                decimalScale={2}
                thousandSeparator
                inputMode="decimal"
                fixedDecimalScale
                allowNegative={false}
                disabled={loading}
                isAllowed={(value) => {
                  if (balance === undefined) {
                    return false;
                  }
                  if (value && value.floatValue) {
                    return value && value?.floatValue <= balance!;
                  }
                  return true;
                }}
                placeholder="Enter Amount"
                style={{
                  borderRadius: 5,
                  height: 40,
                  color: "black",
                  width: "95%",
                  fontSize: 16,
                }}
              />

              <div style={{ display: "flex" }}>
                <BlockyBoldText
                  style={{
                    marginTop: 7,
                    display: "flex",
                    alignItems: "flex-end",
                  }}
                >
                  Odds: &nbsp;
                  <ChangeHighlight
                    hideAfter={1000}
                    highlightClassName={styles.highlight}
                  >
                    <div
                      style={{ color: secondaryColor, fontSize: 16 }}
                    >{`${odds.toFixed(2)}X`}</div>
                  </ChangeHighlight>
                </BlockyBoldText>
              </div>
            </div>
            {/* right */}
            <div style={{ width: "100%", flex: 0.5 }}>
              <Button
                style={{
                  width: "100%",
                  height: 41,
                  backgroundColor: secondaryColor,
                  color: "black",
                }}
                disabled={
                  loading ||
                  !betAmount ||
                  betAmount === "$0.00" ||
                  (balance !== undefined &&
                    bets &&
                    contestEntryId !== undefined &&
                    balance - formattedCurrencyToNumber(betAmount) <
                      contestMinAmount) ||
                  (bets &&
                    contestEntryId !== undefined &&
                    formattedCurrencyToNumber(betAmount) <
                      contestMinBetAmount &&
                    contestMinBetAmount > 0)
                }
                onClick={handleSubmit}
              >
                {loading && (
                  <Spinner
                    animation="border"
                    size="sm"
                    style={{ marginRight: 10 }}
                  />
                )}
                Confirm
              </Button>

              <div style={{ marginTop: 7 }}>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <BlockyBoldText>Exp. Return: &nbsp;</BlockyBoldText>
                  <BlockyBoldText className={styles.text}>
                    <ChangeHighlight
                      hideAfter={1000}
                      highlightClassName={styles.highlight}
                    >
                      <span style={{ color: secondaryColor, fontSize: 16 }}>
                        {formattedProjectedPayout}
                      </span>
                    </ChangeHighlight>
                  </BlockyBoldText>
                </div>
              </div>
            </div>
          </div>
          {/* end form */}
        </div>
        <BlockyMediumText style={{ fontSize: 9, color: "gray" }}>
          Expected returns are subject to change based on subsequent activity in
          the pool.
        </BlockyMediumText>
      </div>
    </div>
  );
};

export default HomepageBetForm;
